import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeWithRelative, messages, type Activity } from '@nesc/shared';
import { api, errorMessage, type ListResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { useUrlFilters } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SkeletonRows } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { cn } from '@/lib/utils';

/** S53 次回アクション。期限超過を最上部に固定する */
export const ActivitiesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const { filters, setFilter } = useUrlFilters({ ownerId: '', overdueOnly: '' });

  const query = {
    type: 'next_action',
    openOnly: 'true',
    ownerId: filters.ownerId || undefined,
    overdueOnly: filters.overdueOnly || undefined,
    limit: 100,
  };

  const { data, isLoading } = useQuery({
    queryKey: qk.activities(query),
    queryFn: () => api.get<ListResponse<Activity>>('/activities', query),
  });

  const complete = useMutation({
    mutationFn: (id: string) => api.patch(`/activities/${id}`, { done: true }),
    onSuccess: () => {
      toast.success('完了にしました');
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const overdue = (data?.data ?? []).filter((a) => a.isOverdue);
  const upcoming = (data?.data ?? []).filter((a) => !a.isOverdue);
  const overdueCount = (data?.meta?.overdueCount as number) ?? overdue.length;

  const renderItem = (activity: Activity) => (
    <li key={activity.id}>
      <Card className={cn(activity.isOverdue && 'border-2 border-foreground')}>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          <div className="min-w-0 flex-1 space-y-0.5">
            <button
              type="button"
              onClick={() => navigate(`/companies/${activity.companyId}`)}
              className="truncate text-body font-semibold underline-offset-2 hover:underline"
            >
              {activity.companyName}
            </button>
            <p className="text-body">{activity.title}</p>
            <p
              className={cn(
                'tabular text-note',
                activity.isOverdue ? 'font-semibold text-foreground' : 'text-foreground-muted',
              )}
            >
              期限 {formatDateTimeWithRelative(activity.dueAt)}
              {activity.owner?.name && ` ・ 担当 ${activity.owner.name}`}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => complete.mutate(activity.id)}
            disabled={complete.isPending}
          >
            <Check aria-hidden className="size-4" />
            {messages.activity.complete}
          </Button>
        </CardContent>
      </Card>
    </li>
  );

  return (
    <>
      <PageHeader
        title={messages.activity.title}
        description={
          overdueCount > 0 ? (
            <span className="font-semibold text-foreground">
              {messages.activity.overdueCount(overdueCount)}
            </span>
          ) : undefined
        }
        actions={
          hasRole('manager') && (
            <Button
              variant="outline"
              onClick={() => setFilter('ownerId', filters.ownerId === 'all' ? '' : 'all')}
            >
              {filters.ownerId === 'all' ? messages.activity.mineOnly : messages.activity.showAll}
            </Button>
          )
        }
      />

      {isLoading ? (
        <SkeletonRows rows={5} />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          title={messages.empty.activities.title}
          description={messages.empty.activities.description}
          action={{ label: messages.empty.activities.action, onClick: () => navigate('/companies') }}
          icon={<ListChecks className="size-8" />}
        />
      ) : (
        <div className="space-y-5">
          {overdue.length > 0 && (
            <section>
              <h2 className="mb-2 text-h3">{messages.activity.overdue}（{overdue.length}）</h2>
              <ul className="space-y-2">{overdue.map(renderItem)}</ul>
            </section>
          )}
          {upcoming.length > 0 && (
            <section>
              <h2 className="mb-2 text-h3">予定（{upcoming.length}）</h2>
              <ul className="space-y-2">{upcoming.map(renderItem)}</ul>
            </section>
          )}
        </div>
      )}
    </>
  );
};
